import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Btn, Card, Input, Textarea, RoleBadge, Attachment } from "@/components/shared";
import { Send, ImagePlus, MessageSquare, ArrowLeft, Sparkles, Camera } from "lucide-react";
import { CameraCapture } from "@/components/CameraCapture";
import { Lightbox } from "@/components/Lightbox";

export default function Messages() {
  const { user } = useAuth();
  return user?.role === "admin" ? <AdminMessages meId={user.id} /> : <UserMessages meId={user.id} />;
}

function UserMessages({ meId }) {
  return (
    <div className="space-y-6 h-full">
      <div className="animate-rise">
        <div className="font-mono text-xs uppercase tracking-widest text-[#FF5722] mb-1">Direct Chat</div>
        <h1 className="font-head font-black uppercase text-3xl sm:text-4xl text-white tracking-tight">Messages</h1>
      </div>
      <MessageThread meId={meId} getUrl="/messages" postUrl="/messages" title="Dispatch / Admin" subtitle="Swift Signings & Services" />
    </div>
  );
}

function AdminMessages({ meId }) {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);

  const loadThreads = useCallback(() => api.get("/messages/threads").then((r) => setThreads(r.data)).catch(() => {}), []);
  useEffect(() => { loadThreads(); const t = setInterval(loadThreads, 6000); return () => clearInterval(t); }, [loadThreads]);

  return (
    <div className="space-y-6">
      <div className="animate-rise">
        <div className="font-mono text-xs uppercase tracking-widest text-[#FF5722] mb-1">Inbox</div>
        <h1 className="font-head font-black uppercase text-3xl sm:text-4xl text-white tracking-tight">Messages</h1>
      </div>
      <div className="grid md:grid-cols-[320px_1fr] gap-4">
        {/* Thread list */}
        <Card className={`p-0 overflow-hidden ${active ? "hidden md:block" : ""}`}>
          <div className="p-3 border-b-2 border-zinc-800 font-mono text-[10px] uppercase tracking-widest text-[#FF5722]">Conversations</div>
          <div className="max-h-[65vh] overflow-y-auto">
            {threads.map((t) => (
              <button key={t.user_id} data-testid={`thread-${t.user_id}`} onClick={() => setActive(t)}
                className={`w-full text-left p-3 border-b border-zinc-800 hover:bg-zinc-900 transition-colors flex items-center gap-3 ${active?.user_id === t.user_id ? "bg-zinc-900" : ""}`}>
                <div className="w-9 h-9 bg-zinc-800 border-2 border-zinc-700 flex items-center justify-center font-head font-black text-[#FF5722] shrink-0">{t.name?.[0]?.toUpperCase()}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2"><span className="text-white font-semibold text-sm truncate">{t.name}</span><RoleBadge role={t.role} /></div>
                  <div className="font-mono text-[11px] text-zinc-500 truncate">{t.last_message ? (t.last_message.text || "📷 Photo") : "No messages yet"}</div>
                </div>
                {t.unread > 0 && <span className="bg-[#FF5722] text-black text-[10px] font-mono font-black w-5 h-5 flex items-center justify-center border border-black">{t.unread}</span>}
              </button>
            ))}
          </div>
        </Card>

        {/* Conversation */}
        <div className={active ? "" : "hidden md:block"}>
          {active ? (
            <MessageThread key={active.user_id} meId={meId} getUrl={`/messages/threads/${active.user_id}`} postUrl={`/messages/threads/${active.user_id}`}
              title={active.name} subtitle={active.email} suggestFor={active.user_id} onBack={() => setActive(null)} onSent={loadThreads} />
          ) : (
            <Card className="h-full min-h-[300px] flex items-center justify-center text-zinc-600 font-mono"><MessageSquare className="mr-2" size={18} /> Select a conversation</Card>
          )}
        </div>
      </div>
    </div>
  );
}

export function MessageThread({ meId, getUrl, postUrl, title, subtitle, onBack, onSent, suggestFor, height = "h-[70vh]" }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [photoId, setPhotoId] = useState(null);
  const [cam, setCam] = useState(false);
  const [lb, setLb] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const fileRef = useRef();
  const endRef = useRef();
  const scrollRef = useRef();
  const atBottomRef = useRef(true);
  const prevCountRef = useRef(0);
  const lastIdRef = useRef(null);
  const forceNextRef = useRef(false);

  const onScroll = () => {
    const el = scrollRef.current;
    if (el) atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const aiSuggest = async () => {
    setAiBusy(true);
    try { const { data } = await api.post("/ai/reply", { kind: "thread", ref_id: suggestFor }); setText(data.suggestion); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAiBusy(false); }
  };

  const load = useCallback(() => api.get(getUrl).then((r) => setMessages(r.data)).catch(() => {}), [getUrl]);
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    const count = messages.length;
    const lastId = count ? messages[count - 1].id : null;
    const firstLoad = prevCountRef.current === 0 && count > 0;
    const newArrived = lastId !== null && lastId !== lastIdRef.current;
    if (firstLoad || forceNextRef.current || (newArrived && atBottomRef.current)) {
      endRef.current?.scrollIntoView({ behavior: firstLoad ? "auto" : "smooth" });
      atBottomRef.current = true;
    }
    if (newArrived) forceNextRef.current = false;
    prevCountRef.current = count;
    lastIdRef.current = lastId;
  }, [messages]);

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoId(data.id); toast.success("Photo ready to send");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const captureUpload = async (files) => {
    const f = files[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setPhotoId(data.id); toast.success("Photo ready to send");
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() && !photoId) return;
    try {
      await api.post(postUrl, { text, photo_id: photoId });
      forceNextRef.current = true;
      setText(""); setPhotoId(null);
      const ta = document.querySelector('[data-testid="msg-input"]'); if (ta) ta.style.height = "";
      load(); onSent?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const photoMsgs = messages.filter((m) => m.photo_id);

  return (
    <Card className={`flex flex-col ${height}`}>
      <div className="p-3 border-b-2 border-zinc-800 flex items-center gap-2">
        {onBack && <button data-testid="thread-back-btn" onClick={onBack} className="md:hidden text-zinc-400"><ArrowLeft size={18} /></button>}
        <div><div className="font-head font-bold text-white">{title}</div><div className="font-mono text-[11px] text-zinc-500">{subtitle}</div></div>
      </div>
      <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="thread-messages">
        {messages.length === 0 && <div className="text-center text-zinc-600 font-mono text-sm py-10">No messages yet. Say hello 👋</div>}
        {messages.map((m) => {
          const mine = m.sender_id === meId;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] border-2 p-2.5 ${mine ? "bg-[#FF5722] text-black border-black" : "bg-zinc-950 text-white border-zinc-800"}`}>
                <div className="font-mono text-[9px] uppercase tracking-wide opacity-70 mb-0.5">{m.sender_name}</div>
                {m.photo_id && <Attachment src={fileUrl(m.photo_id)} onClick={() => setLb(photoMsgs.findIndex((x) => x.id === m.id))} className="max-w-full mb-1 border border-black/20 cursor-pointer" />}
                {m.text && <div className="text-sm break-words whitespace-pre-wrap">{m.text}</div>}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex items-end gap-2 border-t-2 border-zinc-800 p-3">
        {suggestFor && <button type="button" data-testid="thread-ai-suggest" onClick={aiSuggest} disabled={aiBusy} className="shrink-0 px-3 py-2 border-2 border-[#FF5722]/50 text-[#FF5722] hover:bg-[#FF5722]/10 disabled:opacity-50" title="Suggest reply"><Sparkles size={18} /></button>}
        <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={upload} className="hidden" data-testid="msg-photo-input" />
        <button type="button" data-testid="msg-attach-btn" onClick={() => fileRef.current?.click()} className={`shrink-0 px-3 py-2 border-2 ${photoId ? "border-[#FF5722] text-[#FF5722]" : "border-zinc-700 text-zinc-400"}`}><ImagePlus size={18} /></button>
        <button type="button" data-testid="msg-camera-btn" onClick={() => setCam(true)} className="shrink-0 px-3 py-2 border-2 border-zinc-700 text-zinc-400 hover:border-[#FF5722] hover:text-[#FF5722]"><Camera size={18} /></button>
        <Textarea data-testid="msg-input" value={text} onChange={(e) => { setText(e.target.value); e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 160) + "px"; }} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); } }} rows={1} placeholder="Type a message…" className="flex-1 min-w-0 resize-y min-h-[40px] max-h-40 py-2" />
        <Btn data-testid="msg-send-btn" type="submit" className="shrink-0"><Send size={16} /></Btn>
      </form>
      <CameraCapture open={cam} onClose={() => setCam(false)} onCapture={captureUpload} />
      <Lightbox items={photoMsgs.map((m) => ({ src: fileUrl(m.photo_id), name: `photo_${m.id}` }))} index={lb} setIndex={setLb} onClose={() => setLb(null)} />
    </Card>
  );
}
